import type { User } from '../types/api';

export type AppPermission =
  | 'manageTenants'
  | 'manageUsers'
  | 'manageTenantUsers'
  | 'manageDevices'
  | 'manageCustomers'
  | 'viewServiceCases'
  | 'manageServiceCases'
  | 'editAssetRecords'
  | 'useTechnicianMode'
  | 'useDeviceChat';

const TENANT_ADMIN_ROLES = ['tenant_admin'];

export const isPlatformAdmin = (user?: User | null) => user?.is_superuser === true;

export const canViewTenantContext = (user?: User | null) => isPlatformAdmin(user);

export const hasTenantRole = (user: User | null | undefined, roles: string[]) => (
  Boolean(user?.tenants?.some((tenant) => roles.includes(tenant.role)))
);

export const isTenantAdmin = (user?: User | null) => hasTenantRole(user, TENANT_ADMIN_ROLES);

export const isTechnician = (user?: User | null) => (
  Boolean(user?.tenants?.some((tenant) => tenant.is_technician))
);

export const canOperateTenantService = (user?: User | null) => (
  isPlatformAdmin(user) || isTenantAdmin(user)
);

// Chat und Analyse stehen jeder Person offen, die einem Mandanten zugeordnet
// ist - unabhaengig von der Rolle. Das Backend prueft nur noch die
// Mandantenzugehoerigkeit (chat.py, analysis.py).
export const belongsToAnyTenant = (user?: User | null) => (
  isPlatformAdmin(user) || Boolean(user?.tenants?.length)
);

/**
 * Der Plattform-Administrator arbeitet auf Systemebene (ADR 0002, E1/E2).
 * Er gehoert keinem Mandanten an und hat damit keine eigenen Kunden,
 * Servicefaelle oder Techniker - diese Bereiche zeigen ihm nichts, was ihn
 * betrifft. Anlagen sieht er, weil er sie anlegt und anbindet.
 *
 * Das ist eine Frage der Zustaendigkeit, keine Sicherheitsgrenze: Er ist die
 * hoechste Rechtestufe und koennte die Endpunkte weiterhin aufrufen.
 */
const NUR_MANDANTENEBENE: AppPermission[] = [
  'viewServiceCases',
  'manageServiceCases',
  'manageCustomers',
  'editAssetRecords',
  'useTechnicianMode',
  'useDeviceChat',
];

export const hasPermission = (user: User | null | undefined, permission: AppPermission) => {
  if (isPlatformAdmin(user) && NUR_MANDANTENEBENE.includes(permission)) {
    return false;
  }

  switch (permission) {
    case 'manageTenants':
    case 'manageDevices':
      return isPlatformAdmin(user);
    case 'manageUsers':
      // Darf ueberhaupt Benutzer anlegen und aendern. Der Plattform-
      // Administrator tut das in der Mandantenverwaltung, der Mandanten-Admin
      // unter "Team & Techniker".
      return isPlatformAdmin(user) || isTenantAdmin(user);
    case 'manageTenantUsers':
      // Steuert die Ansicht "Team & Techniker" - die zeigt das Team *eines*
      // Mandanten und ist fuer die Plattformebene ohne Bezug.
      return isTenantAdmin(user);
    case 'manageCustomers':
    case 'viewServiceCases':
    case 'manageServiceCases':
    case 'editAssetRecords':
      return canOperateTenantService(user);
    case 'useDeviceChat':
      return belongsToAnyTenant(user);
    case 'useTechnicianMode':
      return canOperateTenantService(user) || isTechnician(user);
    default:
      return false;
  }
};
